import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { validateId } from './utils';
import type {
  AddManualReconciliationEntriesDto,
  CancelManualReconciliationDto,
  CompleteManualReconciliationDto,
  ManualReconciliationCandidatesDto,
  ManualReconciliationDetailDto,
  ManualReconciliationDto,
  ManualReconciliationsDto,
  RemoveManualReconciliationEntriesDto,
  StartManualReconciliationDto,
  UpdateManualReconciliationDto,
} from '~/swagger/accounting/Api';

export type ManualReconciliationCandidatesFilter = {
  showAll?: boolean;
};

const manualReconciliationsUrl = (bankAccountId: string) =>
  `${envConfig.service.accounting}/company-portal/bank-accounts/${bankAccountId}/manual-reconciliations`;

export class ManualReconciliationsRepository extends BaseRestRepository {
  public async list(bankAccountId: string): Promise<ManualReconciliationsDto> {
    validateId(bankAccountId, 'Bank account');
    const response = await this.fetchWithAuth(manualReconciliationsUrl(bankAccountId));
    return this.getJsonResponse(response);
  }

  public async last(bankAccountId: string): Promise<ManualReconciliationDto | undefined> {
    validateId(bankAccountId, 'Bank account');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/last`, {}, false);
    if (response.status === 404) return undefined;
    return this.getJsonResponse(response);
  }

  public async detail(bankAccountId: string, manualReconciliationId: string): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}`);
    return this.getJsonResponse(response);
  }

  public async candidates(
    bankAccountId: string,
    manualReconciliationId: string,
    filter?: ManualReconciliationCandidatesFilter
  ): Promise<ManualReconciliationCandidatesDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const url = urlWithQuery(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}/candidates`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async start(bankAccountId: string, payload: StartManualReconciliationDto): Promise<ManualReconciliationDto> {
    validateId(bankAccountId, 'Bank account');
    const response = await this.fetchWithAuth(manualReconciliationsUrl(bankAccountId), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async update(
    bankAccountId: string,
    manualReconciliationId: string,
    payload: UpdateManualReconciliationDto
  ): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async addEntries(
    bankAccountId: string,
    manualReconciliationId: string,
    payload: AddManualReconciliationEntriesDto
  ): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async removeEntries(
    bankAccountId: string,
    manualReconciliationId: string,
    payload: RemoveManualReconciliationEntriesDto
  ): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}/transactions`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async complete(
    bankAccountId: string,
    manualReconciliationId: string,
    payload: CompleteManualReconciliationDto
  ): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}/complete`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async cancel(
    bankAccountId: string,
    manualReconciliationId: string,
    payload: CancelManualReconciliationDto
  ): Promise<ManualReconciliationDetailDto> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    const response = await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async delete(bankAccountId: string, manualReconciliationId: string): Promise<void> {
    validateId(bankAccountId, 'Bank account');
    validateId(manualReconciliationId, 'Manual reconciliation');
    await this.fetchWithAuth(`${manualReconciliationsUrl(bankAccountId)}/${manualReconciliationId}`, {
      method: 'DELETE',
    });
  }
}

export const manualReconciliationsRepository = new ManualReconciliationsRepository();
